/**
 * What People shows and how it loads, for the computer's page and the Season pages alike: the group or room's stage read
 * from the address, the kept load, the search, how many are drawn, and the words for the group and its bulk note.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import type { NoteAbout } from "../../../shared/people-notes";
import type { PeopleFilters, PeopleListResponse, RegistrantSummary, RoomStage } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { useShownAgain } from "../../lib/inApp";
import { lastLoaded, load, prefetch, warm } from "../../lib/kept";
import { ago, fmt } from "../../lib/when";
import { downloadCsv, peopleCsv } from "./csv";

/** People shown at first, and added each time the list's end comes into view or Show more is pressed. */
export const PAGE = 60;
const VIEW_KEY = "fr.people.view";

export const GROUP_LABEL: Record<string, string> = {
  all: "Everyone",
  new: "New this week",
  watching: "Watching now",
  need: "Need a word",
  quiet: "Went quiet",
};
export const STAGE_NAME: Record<RoomStage, string> = {
  registered: "Registered",
  came: "Came",
  reached: "Reached the offer",
  clicked: "Clicked the offer",
  nosign: "Not a customer yet",
  signed: "Became customers",
  missed: "Missed it",
};
const STAGES: readonly RoomStage[] = ["registered", "came", "reached", "clicked", "nosign", "signed", "missed"];
const RANGE_WORDS = { "7d": "last 7 days", "30d": "last 30 days" } as const;
export const peopleWord = (n: number) => (n === 1 ? "person" : "people");

/**
 * What People is showing, read from its address: `?group=quiet`, `?group=tag:Buyer`, `?room=<id>&stage=missed`, and
 * from Home's results `&range=30d` for the registrations of that period.
 */
export function filtersOf(search: string): PeopleFilters {
  const q = new URLSearchParams(search);
  const room = q.get("room") ?? "";
  if (room) {
    const st = q.get("stage") ?? "";
    const range = q.get("range");
    return {
      room,
      stage: (STAGES as readonly string[]).includes(st) ? (st as RoomStage) : "registered",
      ...(range === "7d" || range === "30d" ? { range } : {}),
    };
  }
  const group = q.get("group") ?? "";
  return group && group !== "all" ? { group } : {};
}
const keyOf = (f: PeopleFilters) =>
  `people:${f.room ? `room=${f.room}&stage=${f.stage}${f.range ? `&range=${f.range}` : ""}` : f.group ? `group=${f.group}` : ""}`;

/** Load everyone ahead of a tap on the tab. */
export function prefetchPeople(): void {
  warm("people:", () => api.people.list({}));
}

/** Start loading a person's page as a finger lands on their row, so it opens ready. */
export function prefetchPerson(id: string): void {
  prefetch(`person:${id}`, () => api.people.get(id));
}

function savedView(): "faces" | "list" {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "faces";
  } catch {
    return "faces";
  }
}

/** Where they are, in the room the row is about. */
export function whereLine(p: RegistrantSummary): string {
  if (p.watching) return `${p.watching.minute} min into ${p.watching.webinar_title}${p.watching.replay ? ", the replay" : ""}`;
  if (!p.pressed_play) return `in ${p.room_title} · hasn't pressed play`;
  return `in ${p.room_title} · watched ${p.watched_pct}%`;
}
export const lastLine = (p: RegistrantSummary) => (p.watching ? "Now" : p.last_seen_at ? ago(p.last_seen_at) : "");
/** How far in they are, for the ring around their face. */
export const depthOf = (p: Pick<RegistrantSummary, "watching" | "watched_pct">) =>
  p.watching ? Math.round((p.watching.minute / Math.max(1, p.watching.of_minutes)) * 100) : p.watched_pct;

export function usePeople() {
  const { search } = useLocation();
  const toast = useToast();
  const shownAgain = useShownAgain();
  const filters = useMemo(() => filtersOf(search), [search]);
  const key = keyOf(filters);
  const [data, setData] = useState<PeopleListResponse | null>(() => lastLoaded(key));
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  const [view, setViewState] = useState<"faces" | "list">(savedView);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    let alive = true;
    setData(lastLoaded<PeopleListResponse>(key));
    setLimit(PAGE);
    load(key, () => api.people.list(filters))
      .then((d) => {
        if (!alive) return;
        setData(d);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [key, filters, shownAgain]);

  const setView = (v: "faces" | "list") => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // the choice lasts for this visit
    }
  };

  const shown = useMemo(() => {
    const all = data?.people ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((p) => `${p.name} ${p.email} ${p.tags.join(" ")}`.toLowerCase().includes(needle));
  }, [data, q]);

  const rail = data?.rail ?? null;
  const everyone = rail?.groups.find((g) => g.key === "all")?.count ?? 0;
  const room = filters.room ? rail?.rooms.find((r) => r.id === filters.room) : undefined;
  const stage = filters.stage ?? "registered";
  const tag = filters.group?.startsWith("tag:") ? filters.group.slice(4) : "";
  const period = filters.range ? `, ${RANGE_WORDS[filters.range]}` : "";
  const title = room
    ? stage === "nosign"
      ? `Clicked, not a customer yet, in ${room.title}${period}`
      : `${STAGE_NAME[stage]} in ${room.title}${period}`
    : tag
      ? `Tagged ${tag}`
      : (GROUP_LABEL[filters.group ?? "all"] ?? "Everyone");
  const n = shown.length;
  const groupAct: { label: string; about: NoteAbout } | null = room
    ? stage === "nosign"
      ? { label: `Send a note to the ${fmt(n)}`, about: "nosign" }
      : stage === "missed"
        ? { label: `Send the replay to the ${fmt(n)}`, about: "missed" }
        : null
    : filters.group === "quiet"
      ? { label: `Send a note to all ${fmt(n)}`, about: "quiet" }
      : null;

  const exportCsv = () => {
    downloadCsv(peopleCsv(shown), "people");
    toast({ text: `Exporting ${fmt(shown.length)} ${peopleWord(shown.length)}`, icon: "download" });
  };

  return {
    search,
    filters,
    key,
    data,
    failed,
    q,
    setQ,
    view,
    setView,
    limit,
    showMore: () => setLimit((l) => l + PAGE),
    shown,
    total: data?.people.length ?? 0,
    rail,
    everyone,
    room,
    stage,
    title,
    n,
    groupAct,
    clearable: !!(filters.room || filters.group),
    exportCsv,
  };
}
